import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime } from 'rxjs';
import { generateUUID } from 'src/app/core/generators/generateUUID';
import { AuthService } from 'src/app/services/AuthService/auth.service';
import { IGetTipoCarreta } from 'src/app/services/TipoCarretaService/tipoCarreta.model';
import { TipoCarretaService } from 'src/app/services/TipoCarretaService/tipoCarreta.service';
import { UploadService } from 'src/app/services/UploadService/upload.service';
import { ServicoEntradaService } from 'src/app/services/ServicoEntradaService/servicoEntrada.service';
import { ITabelaCarreta } from './listagem-servicos-model';
import { formatarData, formatarPlaca } from 'src/app/core/utils/utilities';
import { IGetReportBase64 } from 'src/app/services/ReportService/report.model';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { ImpressoraService } from 'src/app/services/ImpressoraService/impressora.service';
import { IGetServicosParams } from 'src/app/services/ServicoEntradaService/servicoEntrada.model';

@Component({
  selector: 'app-listagem-servicos',
  templateUrl: './listagem-servicos.component.html',
  styleUrls: ['./listagem-servicos.component.scss']
})
export class ListagemServicosComponent {
  form = this._inicializarFormulario();
  tableData: ITabelaCarreta[] | Array<any> = [];
  maxPages: number = 1;
  expandirCardsMobile: boolean = true;
  isLoading: boolean = false;

  tableColumns = [
    { header: 'Cód. Serviço', key: 'codigoServico', type: 'text' },
    { header: 'Cód. Gate Entrada', key: 'codigoGateEntrada', type: 'text' },
    { header: 'Serviço', key: 'servico', type: 'text' },
    { header: 'Valor', key: 'valorServico', type: 'text' },
    { header: 'Entrada', key: 'entrada', type: 'text' },
    { header: 'Saída', key: 'saida', type: 'text' },
    { header: 'Placa Cavalo', key: 'placaCavalo', type: 'text' },
    { header: 'Motorista', key: 'motorista', type: 'text' },
    {
      header: 'Ações',
      key: 'actions',
      type: 'buttons',
      buttons: [
        {
          label: 'Visualizar',
          img: 'assets/eye-icon.png',
          action: (item: any) => this.visualizarServico(item)
        },
        {
          label: 'Download Ticket',
          img: 'assets/icon-download.svg',
          action: (item: any) => this.downloadTicket(item)
        }
      ]
    }
  ];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private servicoEntradaService: ServicoEntradaService,
    private authService: AuthService,
    private uploadService: UploadService,
    private appExportBase64: AppExportBase64,
    private impressoraService: ImpressoraService
  ) {}

  private _inicializarFormulario() {
    const form = this.fb.group({
      FK_GATE_ENTR: this.fb.control('', { nonNullable: true }),
      NM_MTRT: this.fb.control('', { nonNullable: true }),
      TX_PLCA_CAVA: this.fb.control('', { nonNullable: true }),
      DT_SRVC_ENTR_INIC: this.fb.control('', { nonNullable: true }),
      DT_SRVC_ENTR_FIM: this.fb.control('', { nonNullable: true }),
      PAGE_SIZE: this.fb.control(8, { nonNullable: true }),
      PAGE_NUMBER: this.fb.control(1, { nonNullable: true }),
      FK_MTRT_EXTR: this.fb.control(
        Number(localStorage.getItem('id')) || null,
        { nonNullable: true }
      )
    });
    return form;
  }

  editarServico(item: any) {
    const queryParams = {
      ...item,
      isEditing: true
    };
    this.router.navigate(['motorista-externo/cadastro/servicos'], {
      queryParams: queryParams
    });
  }

  visualizarServico(item: any) {
    const queryParams = {
      ...item,
      isViewing: true
    };
    this.router.navigate(['motorista-externo/cadastro/servicos'], {
      queryParams: queryParams
    });
  }

  ngOnInit() {
    this.getServicos();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.getServicos();
    });
  }

  navegarNovaEntrada() {
    this.router.navigate(['motorista-externo/cadastro/servicos']);
  }

  mudarPagina(page: number) {
    this.form.controls['PAGE_NUMBER'].setValue(page + 1);
    this.getServicos();
  }

  formatarCampos(campo: string | number | Date): string {
    return campo === null || campo === '' ? 'Não Informado' : String(campo);
  }

  protected getServicos() {
    this.isLoading = true;
    let params: IGetServicosParams | any = this.form.value;

    this.servicoEntradaService.GetServicos(params).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.tableData = response.ITEMS.map((item: any) => {
          return {
            codigoServico: item.PK_SRVC_ENTR ?? '---',
            codigoGateEntrada: item.FK_GATE_ENTR ?? '---',
            servico: item.TX_TIPO_SRVC ?? '---',
            valorServico:
              item.VL_SRVC.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              }) ?? '---',
            entrada: formatarData(item.DT_SRVC_ENTR) ?? '---',
            saida: formatarData(item.DT_SRVC_SAID) ?? '---',
            placaCavalo: formatarPlaca(item.TX_PLCA_CAVA) ?? '---',
            motorista: item.NM_MTRT ?? '---'
          };
        });
        this.maxPages = response.TOTAL_PAGES;
      },
      error: (_error: any) => {
        this.isLoading = false;
      }
    });
  }

  downloadExcel(): void {
    this.uploadService.downloadTemplateUpload('carreta');
  }

  permitirEditar(item: any): boolean {
    const status: boolean = item.situacao == 1 ? true : false;
    return status;
  }

  downloadTicket(item: { codigoServico: number; servico?: string }) {
    this.isLoading = true;
    const pk = Number(item.codigoServico);
    const isHig = String(item.servico ?? '')
      .toLowerCase()
      .includes('higieniza');

    this.servicoEntradaService
      .GetReceiptServicoEntradaById(0, 0, pk, isHig)
      .subscribe({
        next: (response: IGetReportBase64) => {
          this.appExportBase64.downloadPDF(response.ReportBase64, 'ticket');
          this.isLoading = false;
        },
        error: _error => {
          this.isLoading = false;
        }
      });
  }

  imprimirTicket(item: { codigoServico: number; servico?: string }) {
    this.isLoading = true;
    const pk = Number(item.codigoServico);
    const isHig = String(item.servico ?? '')
      .toLowerCase()
      .includes('higieniza');

    this.servicoEntradaService
      .GetReceiptServicoEntradaById(0, 1, pk, isHig)
      .subscribe({
        next: (response: IGetReportBase64) => {
          this.isLoading = false;
          this.impressoraService.impressoraTicket(
            response.ReportBase64,
            'text/plain'
          );
        },
        error: _error => {
          this.isLoading = false;
        }
      });
  }
}
